import { Controller } from "@hotwired/stimulus"

// 5-step first-run onboarding overlay. Shows once per user — the
// `onboarding_tour_seen` preference flips to true on dismiss / finish,
// so the controller is a no-op on subsequent visits.
//
// Steps point at landmark CSS classes the editor and dashboard ship
// today. If a target is missing on the current page, the controller
// skips that step gracefully — no need to gate by route.
const STEPS = [
  {
    title:  "Welcome to PCP Maker",
    body:   "Person-Centered Plans, made simple. Let's take a 30-second tour of how the editor is laid out — you can quit any time.",
    target: null
  },
  {
    title:  "Sections sidebar",
    body:   "Every Person-Centered Plan has 18 sections. Click any section in the sidebar to jump there. Drag to reorder.",
    target: ".en-sidebar-nav, nav[aria-label='Sections']"
  },
  {
    title:  "Wizard pane (the conversation)",
    body:   "On the right is the wizard — guided prompts that mirror what a good facilitator would ask. Pick or type your answer; the editor on the left fills in.",
    target: ".en-wiz-pane"
  },
  {
    title:  "Editor pane (the plan taking shape)",
    body:   "On the left is the plan itself. Type freely, and the wizard updates. Use the toolbar at the top to save, preview, or export.",
    target: ".en-editor-pane, main.editor-native-frame"
  },
  {
    title:  "Save and export",
    body:   "Save your plan as an encrypted .sdp file — it lives on your device. Tap Make PDF when you're ready to share. That's it. Happy planning.",
    target: ".editor-toolbar, .en-toolbar"
  }
]

export default class extends Controller {
  static targets = ["overlay", "title", "body", "step", "next", "back", "skip", "finish"]

  connect() {
    this.idx = 0
    this._showStep()
  }

  next() {
    if (this.idx < STEPS.length - 1) {
      this.idx += 1
      this._showStep()
    } else {
      this._finish()
    }
  }

  back() {
    if (this.idx > 0) {
      this.idx -= 1
      this._showStep()
    }
  }

  skip() { this._finish() }

  _showStep() {
    const step = STEPS[this.idx]
    if (this.hasTitleTarget) this.titleTarget.textContent = step.title
    if (this.hasBodyTarget)  this.bodyTarget.textContent  = step.body
    if (this.hasStepTarget)  this.stepTarget.textContent  = `Step ${this.idx + 1} of ${STEPS.length}`
    if (this.hasBackTarget)  this.backTarget.disabled     = this.idx === 0
    if (this.hasNextTarget)  this.nextTarget.hidden       = this.idx === STEPS.length - 1
    if (this.hasFinishTarget) this.finishTarget.hidden    = this.idx !== STEPS.length - 1
    this._highlight(step.target)
  }

  _highlight(selector) {
    document.querySelectorAll(".onboarding-spot").forEach(el => el.classList.remove("onboarding-spot"))
    if (!selector) return
    const el = document.querySelector(selector)
    if (el) {
      el.classList.add("onboarding-spot")
      el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  _finish() {
    document.querySelectorAll(".onboarding-spot").forEach(el => el.classList.remove("onboarding-spot"))
    if (this.hasOverlayTarget) this.overlayTarget.hidden = true
    this.element.remove()
    fetch("/api/preferences", {
      method:  "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify({ preferences: { onboarding_tour_seen: true } })
    }).catch(() => {})
  }
}
